import { z } from 'zod';
import { logger } from '@modules/logger';
import { DBGeneratedCardForEmailSignature } from './../../types/db';
import {
  EmailSignatureLegalDisclaimerId,
  EmailSignatureEnvironmentalFooterId,
  EmailSignatureBannerId,
  EmailSignatureImageFieldId,
} from './../../components/EmailSignaturev2/types';
import { isObjectLike } from '@modules/types';
import { isArray, isEmpty } from 'lodash';
import {
  DBEmailSignatureV2,
  DBEmailSignatureContactDetailsFields,
  DBEmailSignatureContactDetailsField,
  DBEmailSignatureSocialLinks,
  DBEmailSignaturePersonalDetailsFields,
  DBEmailSignatureDesign,
  DBEmailSignatureSocialLinksIndices,
  DBEmailSignatureContactDetailsIndices,
  DBEmailSignatureLegalDisclaimer,
  DBEmailSignatureEnvironmentalFooter,
  DBEmailSignatureBanner,
  DBEmailSignatureSaveContact,
  DBEmailSignatureImage,
  DBEmailSignatureImages,
  DBTeamEmailSignatureBulkCreateRequest,
} from 'types/db';
import {
  EmailSignatureContactDetailsFieldId,
  EmailSignatureSocialLinkId,
} from '@components/EmailSignaturev2/types';
import { EmailSignatureFieldPrefixes } from '@components/EmailSignaturev2/consts';
import { isDBCard } from './card-predicates';

const DBEmailSignatureContactDetailsFieldIdSchema = z.string().refine((value) => value.startsWith('ct_'), {
  message: "The string must start with 'ct_' prefix.",
});

const DBEmailSignatureSocialLinkId = z.string().refine((value) => value.startsWith('sc_'), {
  message: "The string must start with 'sc_' prefix.",
});

const DBEmailSignatureImageId = z.string().refine((value) => value.startsWith('im_'), {
  message: "The string must start with 'im_' prefix.",
});

const DBEmailSignatureBannerId = z.string().refine((value) => value.startsWith('bn_'), {
  message: "The string must start with 'bn_' prefix.",
});

const DBEmailSignatureEnvironmentalFooterId = z.string().refine((value) => value.startsWith('en_'), {
  message: "The string must start with 'en_' prefix.",
});

const DBEmailSignatureLegalDisclaimerId = z.string().refine((value) => value.startsWith('le_'), {
  message: "The string must start with 'le_' prefix.",
});

const DBEmailSignaturePersonalDetailsFieldSchema = z.object({
  job_title: z.string(),
  company: z.string(),
  last_name: z.string(),
  first_name: z.string(),
  headline: z.string(),
});

const DBEmailSignatureContactDetailsFieldSchema = z.object({
  id: DBEmailSignatureContactDetailsFieldIdSchema.optional(),
  type: z.string(),
  label: z.string().optional(),
  value: z.string(),
  base_icon: z.string(),
  field_width: z.string(),
  icon_url: z.string(),
  title: z.string().optional(),
  country_code: z.string().optional(),
  extension: z.string().optional(),
});

const DBEmailSignatureSocialLink = z.object({
  id: DBEmailSignatureSocialLinkId.optional(),
  type: z.string().optional(),
  value: z.string().optional(),
  icon_url: z.string().optional(),
});

const DBEmailSignatureImageSchema = z.object({
  id: DBEmailSignatureImageId,
  external_link: z.string(),
  image_url: z.string(),
  shape: z.string(),
  size: z.string(),
  type: z.string(),
  is_primary: z.boolean(),
  original_image_url: z.string(),
  crop: z.object({}).nonstrict().nullable(),
});

const DBEmailSignatureSchema = z.object({
  id: z.string(),
  title: z.string(),
  org_id: z.union([z.string(), z.undefined()]),
  user_id: z.string(),
  created_at: z.record(z.unknown()),
  updated_at: z.record(z.unknown()),
  recipient_email: z.union([z.string(), z.undefined()]),
  activation_code: z.union([z.string(), z.null(), z.undefined()]),
  activated_at: z.union([z.record(z.unknown()), z.undefined()]),
  send_activation_email_at: z.union([z.record(z.unknown()), z.undefined()]),
  deleted_at: z.union([z.record(z.unknown()), z.undefined()]),
  created_by: z.object({
    id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
  }),
  is_team_email_signature: z.boolean(),
  imported_card_id: z.union([z.string(), z.null(), z.undefined()]),
  prevent_editing_signature: z.union([z.boolean(), z.undefined()]),
  personal_details: DBEmailSignaturePersonalDetailsFieldSchema,
  contact_details: z.record(DBEmailSignatureContactDetailsFieldSchema),
  contact_details_order: z.array(DBEmailSignatureContactDetailsFieldIdSchema),
  social_links: z.record(DBEmailSignatureSocialLink),
  social_links_order: z.array(DBEmailSignatureSocialLinkId),
  design: z.object({
    font: z.string(),
    theme_color: z.string(),
    contact_details_display_icon: z.boolean(),
    contact_details_icon_use_theme_color: z.boolean().optional(),
    contact_details_icon_color: z.string().optional(),
    social_links_fill_style: z.string(),
    social_links_size: z.string(),
    social_links_color_mode: z.string().optional(),
    social_links_color: z.string(),
  }),
  banner: z
    .object({
      id: DBEmailSignatureBannerId,
      image_url: z.string(),
      external_link: z.string(),
      size: z.string(),
      original_image_url: z.string(),
      crop: z.object({}).nonstrict().nullable(),
    })
    .optional(),
  environmental_footer: z
    .object({
      id: DBEmailSignatureEnvironmentalFooterId,
      base_icon: z.string(),
      icon_url: z.string(),
      value: z.string(),
    })
    .optional(),
  legal_disclaimer: z
    .object({
      id: DBEmailSignatureLegalDisclaimerId,
      value: z.string(),
      font_size: z.string(),
    })
    .optional(),
  save_contact: z.object({
    card_id: z.string(),
    button_color_use_theme_color: z.boolean(),
    button_color: z.string().optional(),
    display_save_contact: z.boolean(),
  }),
  images: z.array(DBEmailSignatureImageSchema),
});

export function isDBGeneratedCardForEmailSignature(
  candidate: unknown
): candidate is DBGeneratedCardForEmailSignature {
  const isAnObject = isObjectLike(candidate);
  if (!isAnObject) {
    return false;
  }
  const hasEmailSignatureId = typeof candidate.email_signature_id === 'string';
  const isANormalCard = isDBCard(candidate);

  return isANormalCard && typeof hasEmailSignatureId && candidate.is_invisible === true;
}

export function isDBEmailSignatureV2(candidate: unknown): candidate is DBEmailSignatureV2 {
  try {
    DBEmailSignatureSchema.parse(candidate);
  } catch (error) {
    logger.error(JSON.stringify(error));
  }

  return (
    isObjectLike(candidate) &&
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    (candidate.org_id === undefined || typeof candidate.org_id === 'string') &&
    typeof candidate.user_id === 'string' &&
    isObjectLike(candidate.created_at) &&
    isObjectLike(candidate.updated_at) &&
    (candidate.recipient_email === undefined || typeof candidate.recipient_email === 'string') &&
    (candidate.activation_code === undefined ||
      typeof candidate.activation_code === 'string' ||
      candidate.activation_code === null) &&
    (isObjectLike(candidate.activated_at) || candidate.activated_at === undefined) &&
    (isObjectLike(candidate.send_activation_email_at) || candidate.send_activation_email_at === undefined) &&
    (candidate.deleted_at === undefined || isObjectLike(candidate.deleted_at)) &&
    isObjectLike(candidate.created_by) &&
    typeof candidate.created_by.id === 'string' &&
    typeof candidate.created_by.first_name === 'string' &&
    typeof candidate.created_by.last_name === 'string' &&
    typeof candidate.is_team_email_signature === 'boolean' &&
    (candidate.imported_card_id === undefined ||
      typeof candidate.imported_card_id === 'string' ||
      candidate.imported_card_id === null) &&
    isDBEmailSignaturePersonalDetailsFields(candidate.personal_details) &&
    isDBEmailSignatureContactDetailsFields(candidate.contact_details) &&
    isDBEmailSignatureContactDetailsIndices(candidate.contact_details_order) &&
    isDBEmailSignatureSocialLinks(candidate.social_links) &&
    isDBEmailSignatureSocialLinksIndices(candidate.social_links_order) &&
    isDBEmailSignatureDesign(candidate.design) &&
    isDBEmailSignatureBanner(candidate.banner) &&
    isDBEmailSignatureEnvironmentalFooter(candidate.environmental_footer) &&
    isDBEmailSignatureLegalDisclaimer(candidate.legal_disclaimer) &&
    isDBEmailSignatureSaveContact(candidate.save_contact) &&
    isDBEmailSignatureImages(candidate.images) &&
    (candidate.prevent_editing_signature === undefined ||
      typeof candidate.prevent_editing_signature === 'boolean')
  );
}

export function isDBTeamEmailSignatureBulkCreateRequest(
  candidate: unknown
): candidate is DBTeamEmailSignatureBulkCreateRequest {
  return (
    isObjectLike(candidate) &&
    isObjectLike(candidate.requested_at) &&
    typeof candidate.id === 'string' &&
    (typeof candidate.email_signature_id === 'string' || candidate.email_signature_id === null) &&
    typeof candidate.timezone === 'string' &&
    typeof candidate.user_id === 'string' &&
    typeof candidate.org_id === 'string' &&
    (isObjectLike(candidate.instructions_sent_at) || candidate.instructions_sent_at === null) &&
    (isObjectLike(candidate.blinq_notification_sent_at) || candidate.blinq_notification_sent_at === null)
  );
}

export function isDBEmailSignatureContactDetailsFields(
  candidate: unknown
): candidate is DBEmailSignatureContactDetailsFields {
  if (isObjectLike(candidate)) {
    if (isEmpty(candidate)) return true;
    const objectKeys = Object.keys(candidate);
    const keysAreCorrect = objectKeys.every((key) => isDBEmailSignatureContactDetailsFieldId(key));

    const objectValues = Object.values(candidate);
    const valuesAreCorrect = objectValues.every((value) => isDBEmailSignatureContactDetailsField(value));

    return keysAreCorrect && valuesAreCorrect;
  } else return false;
}

export function isDBEmailSignatureContactDetailsFieldId(
  candidate: unknown
): candidate is EmailSignatureContactDetailsFieldId {
  if (typeof candidate === 'string') {
    if (candidate.startsWith('ct_')) return true;
    return false;
  } else return false;
}

export function isDBEmailSignatureContactDetailsField(
  candidate: unknown
): candidate is DBEmailSignatureContactDetailsField {
  if (isObjectLike(candidate)) {
    return (
      isDBEmailSignatureContactDetailsFieldId(candidate.id) &&
      typeof candidate.type === 'string' &&
      (typeof candidate.label === 'string' || candidate.label === undefined) &&
      typeof candidate.value === 'string' &&
      typeof candidate.base_icon === 'string' &&
      typeof candidate.field_width === 'string' &&
      typeof candidate.icon_url === 'string' &&
      (typeof candidate.title === 'string' || candidate.title === undefined) &&
      (typeof candidate.country_code === 'string' || candidate.country_code === undefined) &&
      (typeof candidate.extension === 'string' || candidate.extension === undefined)
    );
  } else return false;
}

export function isDBEmailSignatureContactDetailsIndices(
  candidate: unknown
): candidate is DBEmailSignatureContactDetailsIndices {
  if (!isArray(candidate)) return false;
  return candidate.every((key) => isDBEmailSignatureContactDetailsFieldId(key));
}

export function isDBEmailSignatureSocialLinkId(candidate: unknown): candidate is EmailSignatureSocialLinkId {
  if (typeof candidate === 'string') {
    if (candidate.startsWith('sc_')) return true;
    return false;
  } else return false;
}

export function isDBEmailSignatureSocialLinksIndices(
  candidate: unknown
): candidate is DBEmailSignatureSocialLinksIndices {
  if (!isArray(candidate)) return false;
  return candidate.every((key) => isDBEmailSignatureSocialLinkId(key));
}

export function isDBEmailSignatureSocialLink(
  candidate: unknown
): candidate is DBEmailSignatureContactDetailsField {
  if (isObjectLike(candidate)) {
    return (
      isDBEmailSignatureSocialLinkId(candidate.id) &&
      typeof candidate.type === 'string' &&
      typeof candidate.value === 'string' &&
      typeof candidate.icon_url === 'string'
    );
  } else return false;
}

export function isDBEmailSignatureSocialLinks(candidate: unknown): candidate is DBEmailSignatureSocialLinks {
  if (isObjectLike(candidate)) {
    if (isEmpty(candidate)) return true;
    const objectKeys = Object.keys(candidate);
    const keysAreCorrect = objectKeys.every((key) => isDBEmailSignatureSocialLinkId(key));

    const objectValues = Object.values(candidate);
    const valuesAreCorrect = objectValues.every((value) => isDBEmailSignatureSocialLink(value));

    return keysAreCorrect && valuesAreCorrect;
  } else return false;
}

export function isDBEmailSignaturePersonalDetailsFields(
  candidate: unknown
): candidate is DBEmailSignaturePersonalDetailsFields {
  if (isObjectLike(candidate)) {
    return (
      typeof candidate.job_title === 'string' &&
      typeof candidate.company === 'string' &&
      typeof candidate.last_name === 'string' &&
      typeof candidate.first_name === 'string' &&
      typeof candidate.headline === 'string'
    );
  } else return false;
}

export function isDBEmailSignatureImageFieldId(candidate: unknown): candidate is EmailSignatureImageFieldId {
  if (typeof candidate === 'string') {
    if (candidate.startsWith('im_')) return true;
    return false;
  } else return false;
}

export function isDBEmailSignatureImage(candidate: unknown): candidate is DBEmailSignatureImage {
  if (candidate === undefined) return true;

  if (isObjectLike(candidate)) {
    return (
      isDBEmailSignatureImageFieldId(candidate.id) &&
      typeof candidate.external_link === 'string' &&
      typeof candidate.image_url === 'string' &&
      typeof candidate.shape === 'string' &&
      typeof candidate.size === 'string' &&
      typeof candidate.type === 'string' &&
      typeof candidate.is_primary === 'boolean' &&
      typeof candidate.original_image_url === 'string' &&
      (isObjectLike(candidate.crop) || candidate.crop === null)
    );
  }
  return false;
}

export function isDBEmailSignatureImages(candidate: unknown): candidate is DBEmailSignatureImages {
  if (!isArray(candidate)) return true;

  return candidate.every((image) => isDBEmailSignatureImage(image));
}

export function isDBEmailSignatureDesign(candidate: unknown): candidate is DBEmailSignatureDesign {
  if (!isObjectLike(candidate)) return false;

  return (
    typeof candidate.font === 'string' &&
    typeof candidate.theme_color === 'string' &&
    typeof candidate.contact_details_display_icon === 'boolean' &&
    typeof candidate.contact_details_icon_use_theme_color === 'boolean' &&
    (candidate.contact_details_icon_color === undefined ||
      typeof candidate.contact_details_icon_color === 'string') &&
    typeof candidate.social_links_fill_style === 'string' &&
    typeof candidate.social_links_size === 'string' &&
    typeof candidate.social_links_color_mode === 'string' &&
    (candidate.social_links_color === undefined || typeof candidate.social_links_color === 'string')
  );
}

export function isDBEmailSignatureLegalDisclaimerId(
  candidate: unknown
): candidate is EmailSignatureLegalDisclaimerId {
  if (typeof candidate === 'string') {
    if (candidate.startsWith(EmailSignatureFieldPrefixes.LEGAL_DISCLAIMER)) return true;
    return false;
  } else return false;
}

export function isDBEmailSignatureLegalDisclaimer(
  candidate: unknown
): candidate is DBEmailSignatureLegalDisclaimer {
  if (candidate === undefined) return true;
  if (!isObjectLike(candidate)) return false;

  return (
    isDBEmailSignatureLegalDisclaimerId(candidate.id) &&
    typeof candidate.value === 'string' &&
    typeof candidate.font_size === 'string'
  );
}

export function isDBEmailSignatureEnvironmentalFooterId(
  candidate: unknown
): candidate is EmailSignatureEnvironmentalFooterId {
  if (typeof candidate === 'string') {
    if (candidate.startsWith(EmailSignatureFieldPrefixes.ENVIRONMENTAL_FOOTER)) return true;
    return false;
  } else return false;
}

export function isDBEmailSignatureEnvironmentalFooter(
  candidate: unknown
): candidate is DBEmailSignatureEnvironmentalFooter {
  if (candidate === undefined) return true;
  if (!isObjectLike(candidate)) return false;

  return (
    isDBEmailSignatureEnvironmentalFooterId(candidate.id) &&
    typeof candidate.base_icon === 'string' &&
    typeof candidate.icon_url === 'string' &&
    typeof candidate.value === 'string'
  );
}

export function isDBEmailSignatureBannerId(candidate: unknown): candidate is EmailSignatureBannerId {
  if (typeof candidate === 'string') {
    if (candidate.startsWith(EmailSignatureFieldPrefixes.BANNER)) return true;
    return false;
  } else return false;
}

export function isDBEmailSignatureBanner(candidate: unknown): candidate is DBEmailSignatureBanner {
  if (candidate === undefined) return true;
  if (!isObjectLike(candidate)) return false;

  return (
    isDBEmailSignatureBannerId(candidate.id) &&
    typeof candidate.image_url === 'string' &&
    typeof candidate.external_link === 'string' &&
    typeof candidate.size === 'string' &&
    typeof candidate.original_image_url === 'string' &&
    (isObjectLike(candidate.crop) || candidate.crop === null)
  );
}

export function isDBEmailSignatureSaveContact(candidate: unknown): candidate is DBEmailSignatureSaveContact {
  if (!isObjectLike(candidate)) return false;

  return (
    typeof candidate.card_id === 'string' &&
    typeof candidate.button_color_use_theme_color === 'boolean' &&
    (typeof candidate.button_color === 'string' || candidate.button_color === undefined) &&
    typeof candidate.display_save_contact === 'boolean'
  );
}
